import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MapSelector.css';

// Fix for default marker icons in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create a custom red marker icon
const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

interface MapSelectorProps {
  latitude?: string | number;
  longitude?: string | number;
  onLocationSelect: (lat: number, lng: number) => void;
  height?: string;
  disabled?: boolean;
}

// Component to handle map clicks
const MapClickHandler: React.FC<{
  onLocationSelect: (lat: number, lng: number) => void;
  disabled?: boolean;
}> = ({ onLocationSelect, disabled }) => {
  useMapEvents({
    click(e: any) {
      if (!disabled) {
        const { lat, lng } = e.latlng;
        onLocationSelect(lat, lng);
      }
    },
  });
  return null;
};

const MapSelector: React.FC<MapSelectorProps> = ({
  latitude,
  longitude,
  onLocationSelect,
  height = '300px',
  disabled = false
}) => {
  const [position, setPosition] = useState<[number, number] | null>(null);
  const [center, setCenter] = useState<[number, number]>([37.7749, -122.4194]); // Default: San Francisco
  const [zoom, setZoom] = useState(13);

  useEffect(() => {
    const lat = typeof latitude === 'string' ? parseFloat(latitude) : latitude;
    const lng = typeof longitude === 'string' ? parseFloat(longitude) : longitude;


    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
      const newPosition: [number, number] = [lat, lng];
      setPosition(newPosition);
      setCenter(newPosition);
      setZoom(15); // Zoom in more when we have specific coordinates
    } else {
      setPosition(null);
      // Only reset center if we don't have valid coordinates
      // This prevents the map from jumping back to default when clearing coordinates
      if (!lat && !lng) {
        setCenter([37.7749, -122.4194]); // Default: San Francisco
        setZoom(13); // Default zoom level
      }
    }
  }, [latitude, longitude]);

  const handleLocationSelect = (lat: number, lng: number) => {
    const newPosition: [number, number] = [lat, lng];
    setPosition(newPosition);
    onLocationSelect(lat, lng);
  };

  return (
    <div 
      className={`map-selector-container ${disabled ? 'map-selector-disabled' : ''}`}
      style={{ 
        height, 
        width: '100%'
      }}
    >
      <MapContainer
        key={`${center[0]}-${center[1]}-${zoom}`} // Force re-render when center or zoom changes
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={!disabled}
        dragging={!disabled}
        touchZoom={!disabled}
        doubleClickZoom={!disabled}
        zoomControl={!disabled}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {!disabled && (
          <MapClickHandler 
            onLocationSelect={handleLocationSelect} 
            disabled={disabled}
          />
        )}
        
        {position && (
          <Marker position={position} icon={redIcon} />
        )}
      </MapContainer>
      
      {!disabled && (
        <div className="map-selector-hint">
          Click on the map to select coordinates
        </div>
      )}
    </div>
  );
};

export default MapSelector;
